/** @jsxImportSource @emotion/react */

import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $convertFromMarkdownString, TRANSFORMERS } from '@lexical/markdown';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { Typography } from '@mui/material';
import { Box, SxProps, Theme } from '@mui/system';
import {
    $createParagraphNode,
    $createTextNode,
    $getRoot,
    $insertNodes,
    COMMAND_PRIORITY_HIGH,
    EditorState,
    LexicalEditor,
    PASTE_COMMAND
} from 'lexical';
import { Fragment, MouseEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ContactOptionRenderer from '../../person/ContactOptionRenderer';
import { V } from '../Layout';
import { useStyling } from '../Theme';
import TranslateButton from '../components/translation/TranslateButton';
import { Contact } from '../components/types';
import { ENTITY_CONSTANTS } from '../constants/entityConstants';
import { useShallowMergeSxProps } from '../hooks/useShallowMergeSxProps';
import { truncateText } from '../utils/commonUtils';
import CodeHighlightPlugin from './CodeHighlightPlugin';
import LexicalBottomToolbar from './LexicalBottomToolbar';
import { lexicalTheme } from './LexicalEditorTheme';
import type { LexicalSlashMacroAction } from './LexicalSlashMacroPlugin';
import LexicalSlashMacroPlugin from './LexicalSlashMacroPlugin';
import LexicalTopToolbar from './LexicalTopToolbar';
import MentionsPlugin, { MentionOption } from './MentionsPlugin';
import TableCellDropdown from './TableCellDropdown';
import { isBlackOrWhiteRegex } from './lexicalConstants';
import {
    AVAILABLE_NODES,
    EMPTY_EDITOR_STATE,
    MATCHERS,
    REGEX_FOR_HTML,
    findFirstTextLine
} from './lexicaljsEditorUtils';
import useEditorFocus from './useEditorFocus';
import { useLexicalIsTextContentEmpty } from './useLexicalIsTextContentEmpty';

type LexicaljsEditorProps = {
    id?: string;
    content?: string;
    onChange?: (content: string | object, title?: string) => void;
    onSave?: (
        title: string,
        content: string,
        event?: MouseEvent<HTMLButtonElement>,
        jsonContent?: string
    ) => Promise<any>;
    onCancel?: (event?: MouseEvent<HTMLButtonElement>) => Promise<any>;
    onDelete?: (id?: string) => Promise<any>;
    readOnly?: boolean;
    collapsed?: boolean;
    isSmall?: boolean;
    clearOnSave?: boolean;
    clearOnCancel?: boolean;
    contentPlaceholder?: string;
    header?: ReactNode;
    footer?: ReactNode;
    getTextFromContent?: (value: string) => void;
    getHTMLFromContent?: (value: string) => void;
    getJSONFromContent?: (value: string) => void;
    isEditing?: boolean;
    allowEmpty?: boolean;
    sx?: SxProps;
    resetEditor?: boolean;
    isJsonContent?: boolean;
    isMarkdownContent?: boolean;
    shouldAutoFocus?: boolean;
    focusTrigger?: number;
    maxLines?: number;
    isExpanded?: boolean;
    enableMaxLineClamp?: boolean;
    disableTranslation?: boolean;
    hideToolbar?: boolean;
    slashMacroActions?: ReadonlyArray<LexicalSlashMacroAction>;
    autoOpenSlashMacro?: boolean;
    enableSlashMacroAutoOpenOnRootClick?: boolean;
    suppressAutoOpenSlashMacro?: boolean;
};

type EditorProps = LexicaljsEditorProps & {
    onTranslated?: (translatedText: string) => void;
};

const EMPTY_SLASH_MACRO_ACTIONS: ReadonlyArray<LexicalSlashMacroAction> = [];

// Lexical React plugins are React components, which makes them
// highly composable. Furthermore, you can lazy load plugins if
// desired, so you don't pay the cost for plugins until you
// actually use them.
function MyCustomAutoFocusPlugin({ focusTrigger = 0 }: { focusTrigger?: number }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        // Focus the editor when the effect fires!
        editor.focus();
    }, [editor, focusTrigger]);

    return null;
}

function PasteColorFixerPlugin() {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerCommand(
            PASTE_COMMAND,
            (event: ClipboardEvent | KeyboardEvent) => {
                // 1. Get the data immediately
                const clipboardData =
                    event instanceof ClipboardEvent ? event.clipboardData : (event as any).originalEvent?.clipboardData;

                if (!clipboardData) return false;

                const html = clipboardData.getData('text/html');
                if (!html) return false;

                // 2. Prevent the default "dirty" paste from happening
                event.preventDefault();

                try {
                    // 3. Clean the HTML
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');

                    doc.querySelectorAll('[style]').forEach((el) => {
                        const style = el.getAttribute('style');
                        if (!style) return;

                        if (isBlackOrWhiteRegex.test(style)) {
                            const newStyle = style
                                .split(';')
                                .filter((s) => !s.trim().toLowerCase().startsWith('color:'))
                                .join(';');

                            if (newStyle.trim() === '') {
                                el.removeAttribute('style');
                            } else {
                                el.setAttribute('style', newStyle);
                            }
                        }
                    });

                    // 4. Manually insert the nodes
                    editor.update(() => {
                        // This helper converts your cleaned 'doc' into Lexical nodes
                        const nodes = $generateNodesFromDOM(editor, doc);
                        $insertNodes(nodes);
                    });

                    // 5. Mark as handled so the default listener doesn't run
                    return true;
                } catch (error) {
                    return false;
                }
            },
            COMMAND_PRIORITY_HIGH
        );
    }, [editor]);

    return null;
}

// Catch any errors that occur during Lexical updates and log them
// or throw them as needed. If you don't throw them, Lexical will
// try to recover gracefully without losing user data.
function onError(error) {
    console.error(error);
}

const checkForValidJson = (content: string) => {
    try {
        const jsonVal = JSON.parse(content);
        return jsonVal;
    } catch (error) {
        return null;
    }
};

// TabNode has some non-serializable properties that cause issues when parsing editor state from JSON
// So, we prune it down to relevant properties only during post-processing of template JSON
// detail: 2 flags a node in segemented mode, used to treat a specific character span as a single,
// atomic unit that cannot be partially edited.
const tabNodeJson = { type: 'tab', version: 1, text: '\t', detail: 2 };

const Placeholder = ({ text, color }) => {
    const { theme } = useStyling();
    return (
        <Typography
            className="_editorPlaceholder"
            sx={{
                color: color,
                overflow: 'hidden',
                position: 'absolute',
                textOverflow: 'ellipsis',
                top: 0,
                left: '8px',
                right: '28px',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                pointerEvents: 'none',
                fontSize: '14px'
            }}
        >
            {text}
        </Typography>
    );
};

const setEditorState = (
    editor: LexicalEditor,
    content: string,
    getTextFromContent: (value: string) => void,
    getHTMLFromContent: (value: string) => void,
    getJSONFromContent: (value: string) => void,
    isMarkdownContent = false
) => {
    try {
        if (typeof content === 'string') {
            editor.update(() => {
                if (isMarkdownContent) {
                    $convertFromMarkdownString(content, TRANSFORMERS);
                } else if (checkForValidJson(content)) {
                    let json = checkForValidJson(content);
                    json = postProcessTemplate(editor, json);
                    let editorState: EditorState = null;
                    try {
                        editorState = editor.parseEditorState(json);
                    } catch (e) {
                        console.warn('Failed to parse editor state from JSON');
                    }
                    if (editorState) {
                        if (getTextFromContent) {
                            const editorStateTextString = editorState?.read(() => $getRoot().getTextContent());
                            getTextFromContent(editorStateTextString);
                        }
                        if (getHTMLFromContent) {
                            const htmlstring = editorState.read(() => $generateHtmlFromNodes(editor));
                            getHTMLFromContent(htmlstring);
                        }
                        if (getJSONFromContent) {
                            getJSONFromContent(JSON.stringify(json));
                        }
                        editor.setEditorState(editorState);
                    }
                } else if (REGEX_FOR_HTML.test(content)) {
                    const parser = new DOMParser();
                    const dom = parser.parseFromString(content, 'text/html');
                    const nodes = $generateNodesFromDOM(editor, dom);
                    const root = $getRoot();
                    root.clear();
                    root.append(...nodes);
                } else {
                    const text = $createTextNode(content);
                    const paragraph = $createParagraphNode();
                    const root = $getRoot();
                    root.clear();
                    paragraph.append(text);
                    root.append(paragraph);
                }
            });
        }
    } catch (err) {
        console.log('Failed to initialize Lexical editor content', err);
    }
};
function postProcessTemplate(editor, templateJson) {
    function traverse(nodes) {
        let result = [];
        for (const node of nodes) {
            if (node.type === 'tab') {
                // Prune TabNode to relevant properties only
                result.push(tabNodeJson);
            } else if (node.type === 'richtext' && node.text) {
                const parser = new DOMParser();
                const dom = parser.parseFromString(node.text, 'text/html');
                const lexicalNodes = $generateNodesFromDOM(editor, dom);

                result.push({
                    direction: null,
                    format: '',
                    indent: 0,
                    type: 'paragraph',
                    version: 1,
                    children: traverseLexicalNodes(lexicalNodes, true)
                });
            } else if (node.children) {
                result.push({ ...node, children: traverse(node.children) });
            } else {
                result.push(node);
            }
        }
        return result;
    }
    function traverseLexicalNodes(nodes, setParent = false) {
        return nodes.map((n) => {
            if (setParent) {
                n.__parent = 'root';
            }
            const result = n.exportJSON();
            if (n.getChildrenSize?.() > 0) {
                result.children = traverseLexicalNodes(n.getChildren());
            }
            // Inline prune TabNode to relevant properties only
            if (result.type === 'tab') {
                return tabNodeJson;
            }
            return result;
        });
    }
    return { ...templateJson, root: { ...templateJson.root, children: traverse(templateJson.root.children) } };
}

// const maskCss = 'linear-gradient(to bottom, rgba(0, 0, 0, 1) 0, rgba(0, 0, 0, 1) 40%, rgba(0, 0, 0, 0) 95%, rgba(0, 0, 0, 0) 0) 100% 50% / 100% 100% repeat-x';

const commonContainerSX: SxProps<Theme> = {
    backgroundColor: 'background.paper',
    color: 'text.primary',
    gap: 0.5,
    overflowX: 'hidden',
    overflowY: 'auto',
    position: 'relative',
    maxHeight: '300px',
    borderRadius: 1
};

const richTextContentSX: SxProps<Theme> = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 2,
    width: '100%'
};

export const Editor = ({
    content,
    id,
    isSmall,
    readOnly,
    onSave,
    onChange,
    onCancel,
    onDelete,
    collapsed,
    contentPlaceholder,
    clearOnSave,
    clearOnCancel,
    header,
    footer,
    getTextFromContent,
    getHTMLFromContent,
    getJSONFromContent,
    isEditing,
    allowEmpty,
    sx,
    resetEditor = false,
    isJsonContent = false,
    isMarkdownContent = false,
    shouldAutoFocus = false,
    focusTrigger = 0,
    maxLines = undefined,
    isExpanded,
    enableMaxLineClamp,
    disableTranslation = false,
    hideToolbar = false,
    onTranslated,
    slashMacroActions = EMPTY_SLASH_MACRO_ACTIONS,
    autoOpenSlashMacro = false,
    enableSlashMacroAutoOpenOnRootClick = false,
    suppressAutoOpenSlashMacro = false
}: EditorProps) => {
    const contentFocused = useEditorFocus();
    const { theme, isDarkMode } = useStyling();

    const [editor] = useLexicalComposerContext();
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
    const floatingAnchorRef = useRef<HTMLDivElement | null>(null);
    const [htmlContent, setHtmlContent] = useState<string>('');
    const initialContentRef = useRef<string>(content);
    const prevIsEditingRef = useRef(isEditing);
    const getTextFromContentRef = useRef(getTextFromContent);
    const getHTMLFromContentRef = useRef(getHTMLFromContent);
    const getJSONFromContentRef = useRef(getJSONFromContent);
    const isEmpty = useLexicalIsTextContentEmpty(editor);
    const placeholderColor = theme.palette.text.disabled;
    const previousContentRef = useRef<string>(content);
    const previousIsMarkdownContentRef = useRef<boolean>(isMarkdownContent);

    const containerBaseSx: SxProps<Theme> = useMemo(
        () =>
            readOnly
                ? {
                      ...commonContainerSX,
                      border: `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                          backgroundColor: theme.palette.grey[isDarkMode ? 700 : 100]
                      },
                      p: 1
                  }
                : {
                      ...commonContainerSX,
                      border: `2px solid ${contentFocused ? theme.palette.primary.main : theme.palette.divider}`,
                      minHeight: '140px',
                      height: '100%'
                  },
        [contentFocused, isDarkMode, readOnly, theme.palette.divider, theme.palette.grey, theme.palette.primary.main]
    );

    const containerSx = useShallowMergeSxProps(containerBaseSx, sx);

    const editorStyle = useMemo(() => {
        if (readOnly && !isExpanded && enableMaxLineClamp) {
            return {
                width: '100%',
                padding: 0,
                display: '-webkit-box',
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: 'vertical' as const,
                overflow: 'hidden'
            };
        }

        return {
            width: '100%',
            paddingLeft: readOnly ? 0 : '8px',
            paddingRight: readOnly ? 0 : '8px',
            overflow: 'auto',
            height: '100%'
        };
    }, [enableMaxLineClamp, isExpanded, maxLines, readOnly]);

    useEffect(() => {
        getTextFromContentRef.current = getTextFromContent;
    }, [getTextFromContent]);
    useEffect(() => {
        getHTMLFromContentRef.current = getHTMLFromContent;
    }, [getHTMLFromContent]);
    useEffect(() => {
        getJSONFromContentRef.current = getJSONFromContent;
    }, [getJSONFromContent]);

    useEffect(() => {
        if (!prevIsEditingRef.current && isEditing) {
            // Capture the initial content, when editing just begins.
            initialContentRef.current = content;
        }
        prevIsEditingRef.current = isEditing;
    }, [isEditing, content]);

    useEffect(() => {
        setFloatingAnchorElem(floatingAnchorRef.current);
    }, []);

    useEffect(() => {
        if (!editor) return;
        editor.setEditable(!readOnly);
    }, [editor, readOnly]);

    useEffect(() => {
        if (!editor || !readOnly) {
            setHtmlContent('');
            return;
        }

        const html = editor.getEditorState().read(() => $generateHtmlFromNodes(editor));
        setHtmlContent(html);

        return editor.registerUpdateListener(({ editorState }) => {
            const updatedHtml = editorState.read(() => $generateHtmlFromNodes(editor));
            setHtmlContent(updatedHtml);
        });
    }, [editor, readOnly]);

    const clearState = useCallback(() => {
        if (editor) {
            const editorState: EditorState = editor.parseEditorState(EMPTY_EDITOR_STATE);
            editor.setEditorState(editorState);
        }
    }, [editor]);

    useEffect(() => {
        if (resetEditor) {
            clearState();
        }
    }, [resetEditor, clearState]);

    useEffect(() => {
        if (!editor || typeof content !== 'string') return;

        const didContentChange = previousContentRef.current !== content;
        const didMarkdownModeChange = previousIsMarkdownContentRef.current !== isMarkdownContent;

        if (!didContentChange && !didMarkdownModeChange) {
            return;
        }

        previousContentRef.current = content;
        previousIsMarkdownContentRef.current = isMarkdownContent;
        const currentHtml = editor.getEditorState().read(() => $generateHtmlFromNodes(editor));
        if (didMarkdownModeChange || currentHtml.trim() !== content.trim()) {
            setEditorState(
                editor,
                content,
                getTextFromContentRef.current,
                getHTMLFromContentRef.current,
                getJSONFromContentRef.current,
                isMarkdownContent
            );
        }
    }, [editor, content, isMarkdownContent]);

    useEffect(() => {
        if (editor) {
            if (getHTMLFromContent) {
                // HTML
                const htmlstring = editor.getEditorState().read(() => $generateHtmlFromNodes(editor));
                getHTMLFromContent(htmlstring);
            }
            if (getJSONFromContent) {
                // JSON
                const jsonstring = editor.getEditorState().toJSON();
                getJSONFromContent(JSON.stringify(jsonstring));
            }
        }
    }, [editor, getHTMLFromContent, getJSONFromContent]);

    useEffect(() => {
        if (isEditing) {
            initialContentRef.current = content;
        }
    }, [content, isEditing]);

    const contentEditableNode = (
        <ContentEditable
            className="lexical"
            style={editorStyle}
            onKeyDown={(e) => {
                if (e.key === ' ') e.stopPropagation();
            }}
        />
    );

    const wrappedContentEditable = (
        <Box
            sx={{
                display: 'block',
                flexGrow: 2,
                minHeight: 0,
                width: '100%',
                height: '100%',
                overflow: 'auto'
            }}
            onClick={(event: MouseEvent<HTMLDivElement>) => event.stopPropagation()}
        >
            {contentEditableNode}
        </Box>
    );

    return (
        <V className={'_lexicalContainer'} sx={containerSx} ref={floatingAnchorRef}>
            {header}
            {readOnly || hideToolbar ? (
                <Fragment />
            ) : (
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 3,
                        backgroundColor: theme.palette.background.paper,
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        minHeight: 44,
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <LexicalTopToolbar isSmall={isSmall} disabled={readOnly} />
                </Box>
            )}
            {collapsed && !isEditing ? (
                <></>
            ) : (
                <Box sx={richTextContentSX}>
                    <RichTextPlugin
                        contentEditable={wrappedContentEditable}
                        placeholder={
                            readOnly ? null : <Placeholder text={contentPlaceholder} color={placeholderColor} />
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                </Box>
            )}
            {!readOnly && (
                <>
                    <PasteColorFixerPlugin />
                    <TabIndentationPlugin />
                    <OnChangePlugin
                        ignoreSelectionChange
                        onChange={(editorState: EditorState, editor: LexicalEditor, tags: Set<string>) => {
                            if (typeof onChange === 'function') {
                                editorState.read(() => {
                                    const htmlstring = editor
                                        .getEditorState()
                                        .read(() => $generateHtmlFromNodes(editor));
                                    const newValue = editor.getEditorState().toJSON();
                                    const title = truncateText(findFirstTextLine(newValue.root) || '');
                                    const json = editorState.toJSON();
                                    const content = isJsonContent ? json : htmlstring;
                                    onChange(content, title);
                                    if (getHTMLFromContent) {
                                        getHTMLFromContent(htmlstring);
                                    }
                                });
                                if (getTextFromContent) {
                                    const editorStateTextString = editorState.read(() => $getRoot().getTextContent());
                                    getTextFromContent(editorStateTextString);
                                }
                            }
                        }}
                    />
                    <HistoryPlugin />
                    <AutoLinkPlugin matchers={MATCHERS} />
                    <TablePlugin />
                    {floatingAnchorElem && <TableCellDropdown anchorElem={floatingAnchorElem} />}
                    <LinkPlugin />
                    <LexicalSlashMacroPlugin
                        id={`${id}-slash-macro-plugin`}
                        actions={slashMacroActions}
                        autoOpen={autoOpenSlashMacro}
                        enableAutoOpenOnRootClick={enableSlashMacroAutoOpenOnRootClick}
                        suppressAutoOpen={suppressAutoOpenSlashMacro}
                    />
                    <MentionsPlugin
                        id="lexical-mentions-plugin"
                        trigger={'@'}
                        entity={ENTITY_CONSTANTS.person}
                        rowToOption={(row) => {
                            const names = row.name ? row.name.split(' ') : [];
                            const contact: Contact = {
                                id: row.id,
                                name: {
                                    firstName: names[0],
                                    lastName: names.length > 1 ? names.slice(1).join(' ') : ''
                                },
                                contactInfo: row.contactInfo,
                                personReference: row.reference,
                                organization: row.organization,
                                role: row.role,
                                production: row.production,
                                productionRole: row.productionRole
                            };
                            return new MentionOption<Contact>(
                                contact.id,
                                row.name,
                                contact.contactInfo?.email?.address,
                                contact,
                                <i />
                            );
                        }}
                        OptionRenderer={ContactOptionRenderer}
                    />
                    <ListPlugin />
                    <CheckListPlugin />
                    <CodeHighlightPlugin />
                    {shouldAutoFocus && <MyCustomAutoFocusPlugin focusTrigger={focusTrigger} />}
                </>
            )}
            {readOnly || (!onSave && !onCancel && !onDelete) ? (
                !disableTranslation && readOnly && htmlContent ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
                        <TranslateButton text={htmlContent} format="html" onTranslated={onTranslated} />
                    </Box>
                ) : (
                    <Fragment />
                )
            ) : (
                <LexicalBottomToolbar
                    onSave={
                        typeof onSave === 'function'
                            ? (event: MouseEvent<HTMLButtonElement>) => {
                                  const newValue = editor.getEditorState().toJSON();
                                  const title = truncateText(findFirstTextLine(newValue.root) || '');
                                  const htmlstring = editor.getEditorState().read(() => $generateHtmlFromNodes(editor));
                                  setIsSaving(true);
                                  return onSave(title, htmlstring, event, JSON.stringify(newValue)).then(() => {
                                      setIsSaving(false);
                                      if (clearOnSave) {
                                          clearState();
                                      }
                                  });
                              }
                            : undefined
                    }
                    isSaveDisabled={!allowEmpty ? isEmpty : false}
                    isSaving={isSaving}
                    onDelete={
                        typeof onDelete === 'function'
                            ? () => {
                                  setIsDeleting(true);
                                  return onDelete(id).then(() => {
                                      setIsDeleting(false);
                                  });
                              }
                            : undefined
                    }
                    isDeleteDisabled={false}
                    isDeleting={isDeleting}
                    onCancel={
                        typeof onCancel === 'function'
                            ? async (event: MouseEvent<HTMLButtonElement>) => {
                                  await onCancel(event);

                                  setEditorState(
                                      editor,
                                      initialContentRef.current,
                                      getTextFromContentRef.current,
                                      getHTMLFromContentRef.current,
                                      getJSONFromContentRef.current,
                                      isMarkdownContent
                                  );

                                  if (clearOnCancel) {
                                      clearState();
                                  }
                              }
                            : undefined
                    }
                />
            )}
            {footer}
        </V>
    );
};

export default function LexicaljsEditor({
    id,
    content,
    onChange = undefined,
    onSave = undefined,
    onCancel = undefined,
    onDelete = undefined,
    readOnly = false,
    collapsed = false,
    isSmall = false,
    clearOnSave = false,
    clearOnCancel = false,
    contentPlaceholder = 'Content',
    header = <Fragment />,
    footer = <Fragment />,
    getTextFromContent = undefined,
    getHTMLFromContent = undefined,
    getJSONFromContent = undefined,
    isEditing = false,
    allowEmpty = false,
    sx = {},
    resetEditor = false, // This will clear the editor whenever required
    isJsonContent = false,
    isMarkdownContent = false,
    shouldAutoFocus = false,
    focusTrigger = 0,
    maxLines = 1,
    isExpanded = false,
    enableMaxLineClamp = false,
    disableTranslation = false,
    hideToolbar = false, // Hides the formatting toolbar; formatting stays available via keyboard shortcuts
    slashMacroActions = EMPTY_SLASH_MACRO_ACTIONS,
    autoOpenSlashMacro = false,
    enableSlashMacroAutoOpenOnRootClick = false,
    suppressAutoOpenSlashMacro = false
}: LexicaljsEditorProps) {
    const { theme } = useStyling();
    const initialConfig: InitialConfigType = useMemo(() => {
        return {
            namespace: id,
            editable: !readOnly,
            editorState: (editor: LexicalEditor) =>
                setEditorState(
                    editor,
                    content,
                    getTextFromContent,
                    getHTMLFromContent,
                    getJSONFromContent,
                    isMarkdownContent
                ),
            theme: lexicalTheme(theme),
            onError,
            nodes: [...AVAILABLE_NODES]
        };
    }, [id, readOnly, theme, content, getTextFromContent, getHTMLFromContent, getJSONFromContent, isMarkdownContent]);

    const onTranslated = useCallback(
        (translatedText: string) => {
            if (typeof onChange === 'function') {
                onChange(translatedText);
            }
        },
        [onChange]
    );

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <Editor
                id={id}
                content={content}
                readOnly={readOnly}
                onChange={onChange}
                onSave={onSave}
                onCancel={onCancel}
                onDelete={onDelete}
                collapsed={collapsed}
                isSmall={isSmall}
                clearOnSave={clearOnSave}
                clearOnCancel={clearOnCancel}
                contentPlaceholder={contentPlaceholder}
                header={header}
                footer={footer}
                getTextFromContent={getTextFromContent}
                getHTMLFromContent={getHTMLFromContent}
                getJSONFromContent={getJSONFromContent}
                isEditing={isEditing}
                allowEmpty={allowEmpty}
                sx={sx}
                resetEditor={resetEditor}
                isJsonContent={isJsonContent}
                isMarkdownContent={isMarkdownContent}
                shouldAutoFocus={shouldAutoFocus}
                focusTrigger={focusTrigger}
                maxLines={maxLines}
                isExpanded={isExpanded}
                enableMaxLineClamp={enableMaxLineClamp}
                disableTranslation={disableTranslation}
                hideToolbar={hideToolbar}
                onTranslated={onTranslated}
                slashMacroActions={slashMacroActions}
                autoOpenSlashMacro={autoOpenSlashMacro}
                enableSlashMacroAutoOpenOnRootClick={enableSlashMacroAutoOpenOnRootClick}
                suppressAutoOpenSlashMacro={suppressAutoOpenSlashMacro}
            />
        </LexicalComposer>
    );
}
